import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getOptionGroups, assignProductGroups, setProductAvailability,
  addVariant, updateVariant, deleteVariant,
  getCategories, createCategory,
} from '../services/api';
import type { OptionGroupDto, ProductKind } from '../services/api';
import { useStoreFlags } from '../hooks/useStoreFlags';
import { ImageUploadField } from '../components/ImageUploadField';
import { plainText } from '../utils/plainText';
import { HelpCircle } from 'lucide-react';
import GuidedTour, { TourStep } from '../components/GuidedTour';
import FloatingSaveBar from '../components/FloatingSaveBar';

const PRODUCTS_TOUR: TourStep[] = [
  { target: '[data-tour="product-new"]', title: 'Crea un producto', body: 'Toca este botón verde para agregar un producto nuevo a tu catálogo.', advanceOn: 'click' },
  { target: '[data-tour="product-nombre"]', title: 'Ponle un nombre', body: 'Escribe el nombre del producto. Ej: "Camiseta básica", "Cera para cabello".' },
  { target: '[data-tour="product-precio"]', title: 'Precio de venta', body: 'Pon a cuánto lo vendes. Es lo que la IA le dirá al cliente y lo que se cobra.' },
  { target: '[data-tour="product-crear"]', title: 'Guarda el producto', body: 'Toca "Crear producto" y ¡listo! Ya queda en tu catálogo.', advanceOn: 'click' },
];

// ── Icons ─────────────────────────────────────────────────────────────────────
const SearchIcon  = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const PlusIcon    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const EditIcon    = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const TrashIcon   = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>);
const CloseIcon   = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);
const ImageIcon   = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>);
const TruckIcon   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>);
const SaveIcon    = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>);
const TagIcon     = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>);
const BoxIcon     = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>);

// ── Types ─────────────────────────────────────────────────────────────────────
interface Category {
  categoryId: string;
  name:       string;
  slug:       string;
}

interface Variant {
  variantId:    string;
  name:         string;
  sku:          string | null;
  salePrice:    string | null;
  costPrice:    string | null;
  profitMargin: string | null;
  stock:        number;
  attributes:   Record<string, string>;
  imageUrl:     string | null;
  weight:       string | null;
  sortOrder:    number;
  isActive:     boolean;
}

interface Product {
  productId:        string;
  sku:              string | null;
  name:             string;
  description:      string | null;
  salePrice:        string;
  costPrice:        string;
  profitMargin:     string | null;
  stock:            number;
  hasVariants:      boolean;
  imageUrl:         string | null;
  hasShipping:      boolean;
  weight:           string | null;
  shippingStandard: string | null;
  shippingExpress:  string | null;
  isActive:         boolean;
  createdAt:        string;
  categoryId:       string | null;
  category:         Category | null;
  variants:         Variant[];
  // Módulo restaurante
  kind?:            ProductKind;
  available?:       boolean;
  prepMinutes?:     number | null;
  optionGroups?:    { groupId: string; sortOrder: number }[];
}

interface VariantForm {
  name:       string;
  sku:        string;
  salePrice:  string;
  costPrice:  string;
  stock:      string;
  imageUrl:   string;
  weight:     string;
  attributes: Record<string, string>;
  sortOrder:  string;
}

const EMPTY_VARIANT: VariantForm = {
  name: '', sku: '', salePrice: '', costPrice: '',
  stock: '0', imageUrl: '', weight: '', attributes: {}, sortOrder: '0',
};

interface FormState {
  name:             string;
  sku:              string;
  categoryId:       string;
  description:      string;
  salePrice:        string;
  costPrice:        string;
  stock:            string;
  hasVariants:      boolean;
  imageUrl:         string;
  hasShipping:      boolean;
  weight:           string;
  shippingStandard: string;
  shippingExpress:  string;
  kind:             ProductKind;
  available:        boolean;
  prepMinutes:      string;
}

const EMPTY_FORM: FormState = {
  name: '', sku: '', categoryId: '', description: '',
  salePrice: '', costPrice: '', stock: '0',
  hasVariants: false, imageUrl: '',
  hasShipping: false, weight: '', shippingStandard: '', shippingExpress: '',
  kind: 'stock', available: true, prepMinutes: '',
};

/** El mensaje del servidor tal cual (class-validator lo manda como lista). */
function serverMessage(err: any, fallback: string): string {
  const m = err?.response?.data?.message;
  if (Array.isArray(m)) return m.join('. ');
  return typeof m === 'string' && m ? m : fallback;
}

const isPrepared = (p: Product) => p.kind === 'prepared';

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = (n: string | number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n));

const margin = (sale: string | number, cost: string | number): number | null => {
  const s = Number(sale), c = Number(cost);
  if (!s || !c || s <= 0) return null;
  return Math.round(((s - c) / s) * 100);
};

// ── Profit Badge ──────────────────────────────────────────────────────────────
function ProfitBadge({ sale, cost }: { sale: string | number; cost: string | number }) {
  const m = margin(sale, cost);
  if (m === null) return null;
  const color = m >= 40 ? 'text-green-700 bg-green-50' : m >= 20 ? 'text-amber-700 bg-amber-50' : 'text-red-600 bg-red-50';
  return <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${color}`}>{m}%</span>;
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, onEdit, onDelete, onToggleAvailable }: {
  product:  Product;
  onEdit:   (p: Product) => void;
  onDelete: (p: Product) => void;
  onToggleAvailable?: (p: Product) => void;
}) {
  const prepared       = isPrepared(product);
  const activeVariants = product.variants?.filter(v => v.isActive) ?? [];
  const hasVars        = product.hasVariants && activeVariants.length > 0;
  const minPrice       = hasVars ? Math.min(...activeVariants.map(v => Number(v.salePrice ?? product.salePrice))) : Number(product.salePrice);
  const maxPrice       = hasVars ? Math.max(...activeVariants.map(v => Number(v.salePrice ?? product.salePrice))) : Number(product.salePrice);
  const totalStock     = hasVars ? activeVariants.reduce((s, v) => s + v.stock, 0) : product.stock;
  const profitM        = margin(product.salePrice, product.costPrice);

  return (
    <div className="bg-surface rounded-2xl border border-border-subtle shadow-sm overflow-hidden flex flex-col hover:shadow-md transition">
      <div className="h-44 bg-surface-elevated flex items-center justify-center overflow-hidden relative">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <span className="text-txt-disabled"><ImageIcon /></span>
        )}
        {hasVars && (
          <span className="absolute top-2 right-2 text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full font-medium">
            {activeVariants.length} variantes
          </span>
        )}
        {product.category && (
          <span className="absolute top-2 left-2 text-xs bg-surface/90 text-txt-secondary px-2 py-0.5 rounded-full font-medium border border-border-default">
            {product.category.name}
          </span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-2">
        <div>
          <p className="font-semibold text-txt-primary leading-tight">{product.name}</p>
          {product.sku && <p className="text-xs text-txt-tertiary font-mono">SKU: {product.sku}</p>}
        </div>

        {product.description && (
          <p className="text-xs text-txt-secondary line-clamp-2">{plainText(product.description)}</p>
        )}

        <div className="flex items-center gap-3 mt-auto pt-2 border-t border-slate-50">
          <div>
            <p className="text-xs text-txt-tertiary">Venta</p>
            <p className="font-bold text-txt-primary text-sm">
              {hasVars && minPrice !== maxPrice ? `${fmt(minPrice)} – ${fmt(maxPrice)}` : fmt(minPrice)}
            </p>
          </div>
          {!hasVars && (
            <div>
              <p className="text-xs text-txt-tertiary">Costo</p>
              <p className="text-sm text-txt-secondary">{fmt(product.costPrice)}</p>
            </div>
          )}
          {!hasVars && profitM !== null && (
            <ProfitBadge sale={product.salePrice} cost={product.costPrice} />
          )}
          {prepared ? (
            <div className="ml-auto text-right">
              <p className="text-xs text-txt-tertiary">Preparado</p>
              <p className="text-sm text-txt-secondary">{product.prepMinutes ? `${product.prepMinutes} min` : 'Sin inventario'}</p>
            </div>
          ) : (
            <div className="ml-auto text-right">
              <p className="text-xs text-txt-tertiary">Stock</p>
              <p className={`font-semibold text-sm ${totalStock === 0 ? 'text-red-500' : totalStock < 5 ? 'text-orange-500' : 'text-green-600'}`}>
                {totalStock}
              </p>
            </div>
          )}
        </div>

        {prepared && onToggleAvailable && (
          <div className="flex items-center justify-between gap-2 text-xs text-txt-secondary">
            <span className={product.available === false ? 'font-semibold text-red-500' : ''}>
              {product.available === false ? 'Agotado hoy' : 'Disponible'}
            </span>
            <button type="button" role="switch" aria-checked={product.available === false} aria-label="Agotado"
              onClick={() => onToggleAvailable(product)}
              className={`relative w-10 h-6 rounded-full transition-colors ${product.available === false ? 'bg-red-500' : 'bg-border-default'}`}>
              <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${product.available === false ? 'translate-x-4' : ''}`} />
            </button>
          </div>
        )}

        {hasVars && (
          <div className="flex flex-wrap gap-1">
            {activeVariants.slice(0, 3).map(v => (
              <span key={v.variantId} className="text-xs bg-surface-overlay text-txt-secondary px-2 py-0.5 rounded-full">
                {v.name}
              </span>
            ))}
            {activeVariants.length > 3 && (
              <span className="text-xs bg-surface-overlay text-txt-tertiary px-2 py-0.5 rounded-full">
                +{activeVariants.length - 3}
              </span>
            )}
          </div>
        )}

        {product.hasShipping && (
          <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg w-fit">
            <TruckIcon />
            {product.shippingStandard && Number(product.shippingStandard) > 0
              ? `Envío ${fmt(product.shippingStandard)}`
              : 'Envío disponible'}
          </div>
        )}

        <div className="flex gap-2 mt-1">
          <button onClick={() => onEdit(product)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium text-txt-secondary bg-surface-overlay hover:bg-border-default transition">
            <EditIcon /> Editar
          </button>
          <button onClick={() => onDelete(product)}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-red-400 bg-red-50 hover:bg-red-100 transition">
            <TrashIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Variant Row ───────────────────────────────────────────────────────────────
function VariantRow({ variant, onUpdate, onRemove }: {
  variant:  Variant;
  onUpdate: (id: string, data: Partial<VariantForm>) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const [editing, setEditing]           = useState(false);
  const [form, setForm]                 = useState<VariantForm>({
    name:       variant.name,
    sku:        variant.sku ?? '',
    salePrice:  variant.salePrice ?? '',
    costPrice:  variant.costPrice ?? '',
    stock:      String(variant.stock),
    imageUrl:   variant.imageUrl ?? '',
    weight:     variant.weight ?? '',
    attributes: variant.attributes ?? {},
    sortOrder:  String(variant.sortOrder ?? 0),
  });
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [newAttrKey, setNewAttrKey]     = useState('');
  const [newAttrValue, setNewAttrValue] = useState('');

  const save = async () => {
    setSaving(true); setError('');
    try {
      await onUpdate(variant.variantId, form);
      setEditing(false);
    } catch (err) {
      setError(serverMessage(err, 'No se pudo guardar la variante.'));
    } finally {
      setSaving(false);
    }
  };

  const addAttr = () => {
    if (!newAttrKey.trim() || !newAttrValue.trim()) return;
    setForm(f => ({ ...f, attributes: { ...f.attributes, [newAttrKey.trim()]: newAttrValue.trim() } }));
    setNewAttrKey(''); setNewAttrValue('');
  };

  const removeAttr = (key: string) => {
    const updated = { ...form.attributes };
    delete updated[key];
    setForm(f => ({ ...f, attributes: updated }));
  };

  if (!editing) {
    const attrKeys = Object.keys(variant.attributes ?? {});
    return (
      <div className="px-3 py-2.5 rounded-xl hover:bg-surface-elevated transition">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-2 text-sm">
          <div>
            <p className="font-medium text-txt-primary truncate">{variant.name}</p>
            {attrKeys.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {attrKeys.map(k => (
                  <span key={k} className="text-xs bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded border border-purple-100">
                    {k}: {variant.attributes[k]}
                  </span>
                ))}
              </div>
            )}
          </div>
          <p className="text-txt-secondary font-mono text-xs">{variant.sku ?? '—'}</p>
          <div>
            <p className="font-semibold text-txt-primary">{variant.salePrice ? fmt(variant.salePrice) : 'Base'}</p>
            {variant.costPrice && (
              <div className="flex items-center gap-1">
                <p className="text-xs text-txt-tertiary">{fmt(variant.costPrice)}</p>
                <ProfitBadge sale={variant.salePrice ?? 0} cost={variant.costPrice} />
              </div>
            )}
          </div>
          <p className={`font-semibold text-xs ${variant.stock === 0 ? 'text-red-500' : variant.stock < 5 ? 'text-orange-500' : 'text-green-600'}`}>
            {variant.stock} uds
          </p>
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-blue-600 hover:bg-blue-50 transition">
              <EditIcon />
            </button>
            <button onClick={() => onRemove(variant.variantId)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-red-500 hover:bg-red-50 transition">
              <TrashIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 bg-blue-50/40 rounded-xl space-y-2 border border-blue-100">
      <div className="grid grid-cols-2 gap-2">
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="Nombre de variante *"
          className="col-span-2 px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))}
          placeholder="SKU"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))}
          type="number" placeholder="Stock"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.salePrice} onChange={e => setForm(f => ({ ...f, salePrice: e.target.value }))}
          type="number" placeholder="Precio venta"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.costPrice} onChange={e => setForm(f => ({ ...f, costPrice: e.target.value }))}
          type="number" placeholder="Precio costo"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.weight} onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
          type="number" step="0.01" placeholder="Peso (kg)"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
        <input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
          placeholder="URL imagen"
          className="px-2 py-1.5 text-xs border border-blue-300 bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
      </div>

      <div>
        <p className="text-xs font-semibold text-txt-secondary mb-1">Atributos (color, talla, etc.)</p>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {Object.entries(form.attributes).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {k}: {v}
              <button onClick={() => removeAttr(k)} className="hover:text-red-500">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <input value={newAttrKey} onChange={e => setNewAttrKey(e.target.value)}
            placeholder="Atributo (ej: color)"
            className="flex-1 px-2 py-1 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
          <input value={newAttrValue} onChange={e => setNewAttrValue(e.target.value)}
            placeholder="Valor (ej: Rojo)"
            className="flex-1 px-2 py-1 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
          <button onClick={addAttr} type="button"
            className="px-2 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
            +
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-1 pt-1">
        <button onClick={save} disabled={saving || !form.name.trim()}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium text-[#0A0A0F] disabled:opacity-50 transition"
          style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
          {saving
            ? <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
            : <><SaveIcon /> Guardar</>}
        </button>
        <button onClick={() => setEditing(false)}
          className="px-3 py-1.5 rounded-lg text-xs text-txt-secondary hover:bg-border-default transition">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Product Modal ─────────────────────────────────────────────────────────────
function ProductModal({ product, categories, onClose, onSaved, onCategoryCreated, kitchenEnabled, groups }: {
  product:           Product | null;
  categories:        Category[];
  onClose:           () => void;
  onSaved:           (p: Product) => void;
  onCategoryCreated: (c: Category) => void;
  kitchenEnabled:    boolean;
  groups:            OptionGroupDto[];
}) {
  // Si se creó el producto pero falló la asignación de grupos, el modal sigue abierto
  // editando ESE producto: un segundo "Guardar" no debe crear un duplicado.
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Stock que vio este formulario. Al guardar, el stock solo se manda si el admin lo cambió, y
  // con este valor: si entre medias se vendió algo, la API responde 409 en vez de pisarlo.
  const [stockSeen, setStockSeen] = useState<number | null>(product ? product.stock : null);
  const targetId = product?.productId ?? createdId;
  const isEdit = !!product;
  const initialGroupIds = (product?.optionGroups ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder).map(g => g.groupId);
  const [groupIds, setGroupIds] = useState<string[]>(initialGroupIds);
  const [savedGroupIds, setSavedGroupIds] = useState<string[]>(initialGroupIds);

  const [form, setForm] = useState<FormState>(product ? {
    name:             product.name,
    sku:              product.sku ?? '',
    categoryId:       product.categoryId ?? '',
    description:      product.description ?? '',
    salePrice:        product.salePrice,
    costPrice:        product.costPrice,
    stock:            String(product.stock),
    hasVariants:      product.hasVariants,
    imageUrl:         product.imageUrl ?? '',
    hasShipping:      product.hasShipping,
    weight:           product.weight ?? '',
    shippingStandard: product.shippingStandard ?? '',
    shippingExpress:  product.shippingExpress ?? '',
    kind:             product.kind ?? 'stock',
    available:        product.available ?? true,
    prepMinutes:      product.prepMinutes != null ? String(product.prepMinutes) : '',
  } : EMPTY_FORM);
  const prepared = kitchenEnabled && form.kind === 'prepared';

  const [variants,      setVariants]    = useState<Variant[]>(product?.variants ?? []);
  const [newVariant,    setNewVariant]  = useState<VariantForm>(EMPTY_VARIANT);
  const [newAttrKey,    setNewAttrKey]  = useState('');
  const [newAttrValue,  setNewAttrValue]= useState('');
  const [addingVariant, setAddingVariant] = useState(false);
  const [saving,        setSaving]      = useState(false);
  const [saved,         setSaved]       = useState(false);
  const [error,         setError]       = useState('');
  const [tab,           setTab]         = useState<'info' | 'variants'>('info');
  const [newCatName,    setNewCatName]  = useState('');
  const [creatingCat,   setCreatingCat] = useState(false);
  const [showCatInput,  setShowCatInput]= useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setTimeout(() => nameRef.current?.focus(), 50); }, []);

  // Snapshot inicial para detectar cambios sin guardar (botón flotante)
  const baselineRef = useRef('');
  useEffect(() => { baselineRef.current = JSON.stringify({ form, variants, groupIds }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = baselineRef.current !== '' && JSON.stringify({ form, variants, groupIds }) !== baselineRef.current;

  const toggleGroup = (id: string) =>
    setGroupIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const moveGroup = (idx: number, dir: -1 | 1) =>
    setGroupIds(ids => {
      const j = idx + dir;
      if (j < 0 || j >= ids.length) return ids;
      const next = [...ids];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const set = (k: keyof FormState, v: any) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  // ── Crear categoría rápida ────────────────────────────────────────────────
  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    setCreatingCat(true);
    try {
      const res = await createCategory(newCatName.trim());
      onCategoryCreated(res.data);
      set('categoryId', res.data.categoryId);
      setNewCatName('');
      setShowCatInput(false);
    } catch {
      setError('Error al crear categoría');
    } finally {
      setCreatingCat(false);
    }
  };

  // ── Guardar producto ──────────────────────────────────────────────────────
  const stockFields = () => {
    if (form.hasVariants || prepared) return {};
    const stock = Number(form.stock) || 0;
    if (!targetId) return { stock };
    if (stock === stockSeen) return {}; // no lo tocó: no se pisa lo vendido mientras tanto
    return { stock, stockBefore: stockSeen ?? undefined };
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return setError('El nombre es requerido');
    if (!form.salePrice)   return setError('El precio de venta es requerido');
    setSaving(true); setError('');
    try {
      const payload: any = {
        name:             form.name.trim(),
        sku:              form.sku.trim() || undefined,
        categoryId:       form.categoryId || undefined,
        description:      form.description.trim() || undefined,
        salePrice:        Number(form.salePrice),
        costPrice:        form.costPrice ? Number(form.costPrice) : undefined,
        ...stockFields(),
        hasVariants:      prepared ? false : form.hasVariants,
        imageUrl:         form.imageUrl.trim() || undefined,
        hasShipping:      form.hasShipping,
        weight:           form.weight ? Number(form.weight) : undefined,
        shippingStandard: form.shippingStandard ? Number(form.shippingStandard) : undefined,
        shippingExpress:  form.shippingExpress  ? Number(form.shippingExpress)  : undefined,
        // Módulo restaurante: solo se envía con el módulo activo (sin él la API no los acepta)
        ...(kitchenEnabled && {
          kind:        form.kind,
          available:   form.available,
          prepMinutes: prepared && form.prepMinutes !== '' ? Number(form.prepMinutes) : null,
        }),
        // CAMBIO 3: en creación, enviar variantes locales en el mismo request
        ...(!targetId && !prepared && form.hasVariants && variants.length > 0 && {
          variants: variants.map(v => ({
            name:       v.name,
            sku:        v.sku || undefined,
            salePrice:  v.salePrice ? Number(v.salePrice) : undefined,
            costPrice:  v.costPrice ? Number(v.costPrice) : undefined,
            stock:      v.stock,
            attributes: v.attributes,
            imageUrl:   v.imageUrl || undefined,
            weight:     v.weight ? Number(v.weight) : undefined,
            sortOrder:  v.sortOrder,
          })),
        }),
      };
      const res = targetId
        ? await updateProduct(targetId, payload)
        : await createProduct(payload);
      const productId: string = res.data.productId;
      if (!targetId) setCreatedId(productId);
      setStockSeen(res.data.stock);

      // Grupos de opciones: después del producto, y solo si cambiaron.
      if (kitchenEnabled && JSON.stringify(groupIds) !== JSON.stringify(savedGroupIds)) {
        try {
          await assignProductGroups(productId, groupIds);
          setSavedGroupIds(groupIds);
        } catch (err) {
          onSaved({ ...res.data, variants, optionGroups: savedGroupIds.map((groupId, sortOrder) => ({ groupId, sortOrder })) });
          setError(`El producto se guardó, pero no sus grupos de opciones: ${serverMessage(err, 'inténtalo de nuevo')}`);
          return;
        }
      }
      onSaved({ ...res.data, variants, optionGroups: groupIds.map((groupId, sortOrder) => ({ groupId, sortOrder })) });
      onClose();
    } catch (err: any) {
      // Se vendió algo mientras editaba: a partir de ahora se compara con el stock actual, así
      // que si vuelve a guardar es porque decidió ese número viendo el aviso.
      const data = err?.response?.data;
      if (data?.code === 'STOCK_CHANGED' && typeof data.current === 'number') setStockSeen(data.current);
      setError(serverMessage(err, 'Error al guardar. Verifica los datos.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Atributos nueva variante ──────────────────────────────────────────────
  const addNewVariantAttr = () => {
    if (!newAttrKey.trim() || !newAttrValue.trim()) return;
    setNewVariant(v => ({ ...v, attributes: { ...v.attributes, [newAttrKey.trim()]: newAttrValue.trim() } }));
    setNewAttrKey(''); setNewAttrValue('');
  };

  // ── Agregar variante ──────────────────────────────────────────────────────
  // CAMBIO 4: en creación agrega localmente; en edición llama a la API
  const handleAddVariant = async () => {
    if (!newVariant.name.trim()) return;

    // En creación: agregar al estado local, se envían al guardar el producto
    if (!isEdit) {
      const tempVariant: Variant = {
        variantId:    `temp-${Date.now()}`,
        name:         newVariant.name.trim(),
        sku:          newVariant.sku || null,
        salePrice:    newVariant.salePrice || null,
        costPrice:    newVariant.costPrice || null,
        profitMargin: null,
        stock:        Number(newVariant.stock) || 0,
        attributes:   newVariant.attributes,
        imageUrl:     newVariant.imageUrl || null,
        weight:       newVariant.weight || null,
        sortOrder:    Number(newVariant.sortOrder) || 0,
        isActive:     true,
      };
      setVariants(v => [...v, tempVariant]);
      setNewVariant(EMPTY_VARIANT);
      setNewAttrKey(''); setNewAttrValue('');
      return;
    }

    // En edición: llamar a la API directamente
    setAddingVariant(true);
    try {
      const res = await addVariant(product!.productId, {
        name:       newVariant.name.trim(),
        sku:        newVariant.sku.trim() || undefined,
        salePrice:  newVariant.salePrice ? Number(newVariant.salePrice) : undefined,
        costPrice:  newVariant.costPrice ? Number(newVariant.costPrice) : undefined,
        stock:      Number(newVariant.stock) || 0,
        imageUrl:   newVariant.imageUrl.trim() || undefined,
        weight:     newVariant.weight ? Number(newVariant.weight) : undefined,
        attributes: newVariant.attributes,
        sortOrder:  Number(newVariant.sortOrder) || 0,
      });
      setVariants(v => [...v, res.data]);
      setNewVariant(EMPTY_VARIANT);
      setNewAttrKey(''); setNewAttrValue('');
    } catch {
      setError('Error al agregar variante');
    } finally {
      setAddingVariant(false);
    }
  };

  const handleUpdateVariant = async (variantId: string, data: Partial<VariantForm>) => {
    // Variante temporal (creación): actualizar solo en estado local
    if (variantId.startsWith('temp-')) {
      setVariants(v => v.map(x => x.variantId === variantId ? {
        ...x,
        name:      data.name ?? x.name,
        sku:       data.sku || null,
        salePrice: data.salePrice || null,
        costPrice: data.costPrice || null,
        stock:     data.stock !== undefined ? Number(data.stock) : x.stock,
        imageUrl:  data.imageUrl || null,
        weight:    data.weight || null,
        attributes: data.attributes ?? x.attributes,
        sortOrder: data.sortOrder !== undefined ? Number(data.sortOrder) : x.sortOrder,
      } : x));
      return;
    }
    // El stock solo viaja si cambió, con el que se vio (409 si se vendió algo entre medias).
    const seen = variants.find(x => x.variantId === variantId)?.stock;
    const stock = data.stock !== undefined ? Number(data.stock) || 0 : undefined;
    const stockChanged = stock !== undefined && stock !== seen;
    let res: Awaited<ReturnType<typeof updateVariant>>;
    try {
      res = await updateVariant(variantId, {
        name:       data.name,
        sku:        data.sku || undefined,
        salePrice:  data.salePrice ? Number(data.salePrice) : undefined,
        costPrice:  data.costPrice ? Number(data.costPrice) : undefined,
        ...(stockChanged && { stock, stockBefore: seen }),
        imageUrl:   data.imageUrl || undefined,
        weight:     data.weight ? Number(data.weight) : undefined,
        attributes: data.attributes,
        sortOrder:  data.sortOrder !== undefined ? Number(data.sortOrder) : undefined,
      });
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.code === 'STOCK_CHANGED' && typeof body.current === 'number')
        setVariants(v => v.map(x => x.variantId === variantId ? { ...x, stock: body.current } : x));
      throw err;
    }
    setVariants(v => v.map(x => x.variantId === variantId ? res.data : x));
  };

  const handleRemoveVariant = async (variantId: string) => {
    // Variante temporal: eliminar solo del estado local
    if (variantId.startsWith('temp-')) {
      setVariants(v => v.filter(x => x.variantId !== variantId));
      return;
    }
    await deleteVariant(variantId);
    setVariants(v => v.filter(x => x.variantId !== variantId));
  };

  const liveMargin = form.salePrice && form.costPrice
    ? margin(form.salePrice, form.costPrice)
    : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between sticky top-0 bg-surface z-10">
          <h2 className="font-bold text-txt-primary">{isEdit ? 'Editar producto' : 'Nuevo producto'}</h2>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-txt-tertiary hover:bg-surface-overlay transition">
            <CloseIcon />
          </button>
        </div>

        {/* CAMBIO 1: tabs visibles cuando isEdit O cuando hasVariants está activo */}
        {(isEdit || form.hasVariants) && !prepared && (
          <div className="flex border-b border-border-subtle px-6">
            {(['info', 'variants'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-txt-tertiary hover:text-txt-secondary'}`}>
                {t === 'info' ? 'Información' : `Variantes (${variants.length})`}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-5 space-y-4">
          {/* ── TAB INFO ───────────────────────────────────────────────────── */}
          {tab === 'info' && (
            <>
              <ImageUploadField
                value={form.imageUrl}
                onChange={url => set('imageUrl', url)}
                label="Imagen del producto"
              />

              <div data-tour="product-nombre">
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Nombre *</label>
                <input ref={nameRef} type="text" value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Ej: Camiseta básica"
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">SKU</label>
                  <input type="text" value={form.sku}
                    onChange={e => set('sku', e.target.value)}
                    placeholder="Ej: CAM-001"
                    className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1 flex items-center gap-1">
                    <TagIcon /> Categoría
                  </label>
                  <div className="flex gap-1">
                    <select value={form.categoryId} onChange={e => set('categoryId', e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-primary">
                      <option value="">Sin categoría</option>
                      {categories.map(c => <option key={c.categoryId} value={c.categoryId}>{c.name}</option>)}
                    </select>
                    <button type="button" onClick={() => setShowCatInput(s => !s)}
                      title="Nueva categoría"
                      className="w-9 h-9 flex items-center justify-center rounded-xl border border-border-default text-txt-tertiary hover:text-blue-600 hover:border-blue-300 transition">
                      <PlusIcon />
                    </button>
                  </div>
                  {showCatInput && (
                    <div className="flex gap-1 mt-1.5">
                      <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                        placeholder="Nueva categoría"
                        className="flex-1 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                      <button onClick={handleCreateCategory} disabled={creatingCat || !newCatName.trim()}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition">
                        {creatingCat ? '...' : 'Crear'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {kitchenEnabled && (
                <div>
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Tipo</label>
                  <select value={form.kind}
                    onChange={e => {
                      const kind = e.target.value as ProductKind;
                      setForm(f => ({ ...f, kind, ...(kind === 'prepared' && { hasVariants: false }) }));
                      setSaved(false);
                      if (kind === 'prepared') setTab('info');
                    }}
                    className="w-full px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-primary">
                    <option value="stock">Con inventario</option>
                    <option value="prepared">Preparado (sin inventario)</option>
                  </select>
                  {prepared && variants.some(v => v.isActive) && (
                    <p className="text-xs text-red-500 mt-1">Este producto tiene variantes: quítalas antes de pasarlo a preparado (usa grupos de opciones).</p>
                  )}
                </div>
              )}

              {/* CAMBIO 2: al activar hasVariants → ir automáticamente al tab variantes */}
              {!prepared && (
              <div className="flex items-center justify-between py-2.5 px-3 rounded-xl border border-border-default bg-surface-elevated">
                <div className="flex items-center gap-2 text-sm text-txt-primary">
                  <BoxIcon />
                  <span>Este producto tiene variantes</span>
                  <span className="text-xs text-txt-tertiary">(tallas, colores, etc.)</span>
                </div>
                <button onClick={() => {
                  const next = !form.hasVariants;
                  set('hasVariants', next);
                  if (next) setTab('variants');
                }}
                  className="w-11 h-6 rounded-full transition relative flex-shrink-0"
                  style={form.hasVariants ? { background: 'linear-gradient(135deg,#9333ea,#2563eb)' } : { background: '#e2e8f0' }}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-surface rounded-full shadow transition-all ${form.hasVariants ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
              )}

              {!form.hasVariants && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div data-tour="product-precio">
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Precio venta *</label>
                      <input type="number" value={form.salePrice}
                        onChange={e => set('salePrice', e.target.value)}
                        placeholder="0"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Precio costo</label>
                      <input type="number" value={form.costPrice}
                        onChange={e => set('costPrice', e.target.value)}
                        placeholder="0"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                    {prepared ? (
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Minutos prep.</label>
                      <input type="number" min={0} max={600} value={form.prepMinutes}
                        onChange={e => set('prepMinutes', e.target.value)}
                        placeholder="Ej: 15"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                    ) : (
                    <div>
                      <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Stock</label>
                      <input type="number" value={form.stock}
                        onChange={e => set('stock', e.target.value)}
                        placeholder="0"
                        className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                    </div>
                    )}
                  </div>
                  {prepared && (
                    <label className="flex items-center gap-2 text-sm text-txt-primary">
                      <input type="checkbox" checked={form.available} onChange={() => set('available', !form.available)} />
                      Disponible hoy (desmárcalo si se agotó)
                    </label>
                  )}
                  {kitchenEnabled && (
                    <div className="space-y-2 pt-1">
                      <p className="text-xs font-semibold text-txt-secondary uppercase tracking-wide">Grupos de opciones</p>
                      {groups.length === 0 ? (
                        <p className="text-xs text-txt-tertiary">Aún no hay grupos. Créalos en la pantalla Menú.</p>
                      ) : (
                        <ul className="space-y-1">
                          {[...groupIds.map(id => groups.find(g => g.groupId === id)).filter((g): g is OptionGroupDto => !!g),
                            ...groups.filter(g => !groupIds.includes(g.groupId))].map(g => {
                            const idx = groupIds.indexOf(g.groupId);
                            const on = idx >= 0;
                            return (
                              <li key={g.groupId} className="flex items-center gap-2 rounded-xl border border-border-subtle px-3 py-1.5">
                                <input type="checkbox" checked={on} onChange={() => toggleGroup(g.groupId)} aria-label={g.name} />
                                <span className="flex-1 text-sm text-txt-primary">{g.name}</span>
                                <span className="text-xs text-txt-tertiary">{g.minSelect >= 1 ? 'Obligatorio' : 'Opcional'}</span>
                                {on && (
                                  <>
                                    <button type="button" aria-label="Subir" disabled={idx === 0} onClick={() => moveGroup(idx, -1)}
                                      className="px-1.5 text-txt-tertiary disabled:opacity-30">↑</button>
                                    <button type="button" aria-label="Bajar" disabled={idx === groupIds.length - 1} onClick={() => moveGroup(idx, 1)}
                                      className="px-1.5 text-txt-tertiary disabled:opacity-30">↓</button>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                  {liveMargin !== null && (
                    <p className={`text-xs font-semibold ${liveMargin >= 40 ? 'text-green-600' : liveMargin >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
                      Margen de ganancia: {liveMargin}%
                    </p>
                  )}
                </div>
              )}

              {form.hasVariants && (
                <div>
                  <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Precio base de referencia *</label>
                  <input type="number" value={form.salePrice}
                    onChange={e => set('salePrice', e.target.value)}
                    placeholder="Precio de referencia"
                    className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <p className="text-xs text-txt-tertiary mt-1">Cada variante puede tener su propio precio en la pestaña Variantes.</p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-txt-secondary uppercase tracking-wide block mb-1">Descripción</label>
                <textarea value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Describe el producto para que la IA pueda informar a clientes..."
                  rows={3} maxLength={50000}
                  className="w-full px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition resize-none" />
                <p className="text-xs text-txt-tertiary text-right mt-0.5">{form.description.length.toLocaleString()} / 50.000</p>
              </div>

              <div className="space-y-3 border border-border-default rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-txt-primary">
                    <TruckIcon /> <span>Configuración de envío</span>
                  </div>
                  <button onClick={() => set('hasShipping', !form.hasShipping)}
                    className="w-11 h-6 rounded-full transition relative flex-shrink-0"
                    style={form.hasShipping ? { background: 'linear-gradient(135deg,#2563eb,#0ea5e9)' } : { background: '#e2e8f0' }}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-surface rounded-full shadow transition-all ${form.hasShipping ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
                {form.hasShipping && (
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    <div>
                      <label className="text-xs text-txt-secondary block mb-1">Peso (kg)</label>
                      <input type="number" step="0.01" value={form.weight}
                        onChange={e => set('weight', e.target.value)}
                        placeholder="1.5"
                        className="w-full px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                    </div>
                    <div>
                      <label className="text-xs text-txt-secondary block mb-1">Envío estándar</label>
                      <input type="number" value={form.shippingStandard}
                        onChange={e => set('shippingStandard', e.target.value)}
                        placeholder="5000"
                        className="w-full px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                    </div>
                    <div>
                      <label className="text-xs text-txt-secondary block mb-1">Envío express</label>
                      <input type="number" value={form.shippingExpress}
                        onChange={e => set('shippingExpress', e.target.value)}
                        placeholder="12000"
                        className="w-full px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                    </div>
                  </div>
                )}
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button onClick={handleSubmit} disabled={saving} data-tour="product-crear"
                className="w-full py-2.5 rounded-xl text-sm font-medium text-[#0A0A0F] disabled:opacity-50 transition"
                style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}>
                {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear producto'}
              </button>
            </>
          )}

          {/* ── TAB VARIANTES ──────────────────────────────────────────────── */}
          {tab === 'variants' && (
            <>
              {!isEdit && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-700">
                  Las variantes se guardarán junto con el producto al hacer clic en <strong>Crear producto</strong>.
                </div>
              )}

              <p className="text-xs text-txt-tertiary">Cada variante tiene su propio precio, stock y atributos.</p>

              {variants.length === 0 ? (
                <p className="text-center text-txt-tertiary text-sm py-6">Sin variantes aún — agrega la primera abajo</p>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] px-3 py-1 text-xs font-semibold text-txt-tertiary uppercase tracking-wide">
                    <span>Nombre</span><span>SKU</span><span>Precio</span><span>Stock</span><span />
                  </div>
                  {variants.map(v => (
                    <VariantRow key={v.variantId} variant={v}
                      onUpdate={handleUpdateVariant}
                      onRemove={handleRemoveVariant} />
                  ))}
                </div>
              )}

              <div className="border-t border-border-subtle pt-4 space-y-2">
                <p className="text-xs font-semibold text-txt-secondary uppercase tracking-wide">Nueva variante</p>
                <div className="grid grid-cols-2 gap-2">
                  <input value={newVariant.name}
                    onChange={e => setNewVariant(v => ({ ...v, name: e.target.value }))}
                    placeholder="Nombre * (ej: Talla M / Rojo)"
                    className="col-span-2 px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.sku}
                    onChange={e => setNewVariant(v => ({ ...v, sku: e.target.value }))}
                    placeholder="SKU (opcional)"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.stock}
                    onChange={e => setNewVariant(v => ({ ...v, stock: e.target.value }))}
                    placeholder="Stock" type="number"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.salePrice}
                    onChange={e => setNewVariant(v => ({ ...v, salePrice: e.target.value }))}
                    placeholder="Precio venta" type="number"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.costPrice}
                    onChange={e => setNewVariant(v => ({ ...v, costPrice: e.target.value }))}
                    placeholder="Precio costo" type="number"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.weight}
                    onChange={e => setNewVariant(v => ({ ...v, weight: e.target.value }))}
                    placeholder="Peso kg" type="number" step="0.01"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                  <input value={newVariant.imageUrl}
                    onChange={e => setNewVariant(v => ({ ...v, imageUrl: e.target.value }))}
                    placeholder="URL imagen"
                    className="px-3 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition" />
                </div>

                <div>
                  <p className="text-xs text-txt-secondary mb-1">Atributos</p>
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {Object.entries(newVariant.attributes).map(([k, v]) => (
                      <span key={k} className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {k}: {v}
                        <button onClick={() => {
                          const a = { ...newVariant.attributes };
                          delete a[k];
                          setNewVariant(nv => ({ ...nv, attributes: a }));
                        }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input value={newAttrKey} onChange={e => setNewAttrKey(e.target.value)}
                      placeholder="Atributo (color, talla…)"
                      className="flex-1 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                    <input value={newAttrValue} onChange={e => setNewAttrValue(e.target.value)}
                      placeholder="Valor (Rojo, M…)"
                      className="flex-1 px-2 py-1.5 text-xs border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-lg focus:outline-none focus:ring-1 focus:ring-lime/30" />
                    <button onClick={addNewVariantAttr} type="button"
                      className="px-2 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">+</button>
                  </div>
                </div>

                <button onClick={handleAddVariant}
                  disabled={addingVariant || !newVariant.name.trim()}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-[#0A0A0F] disabled:opacity-40 transition"
                  style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
                  {addingVariant ? 'Agregando...' : <><PlusIcon /> Agregar variante</>}
                </button>

                {/* Botón guardar también en tab variantes durante creación */}
                {!isEdit && (
                  <button onClick={handleSubmit} disabled={saving}
                    className="w-full py-2.5 rounded-xl text-sm font-medium text-[#0A0A0F] disabled:opacity-50 transition mt-2"
                    style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}> 
                    {saving ? 'Guardando...' : `Crear producto${variants.length > 0 ? ` con ${variants.length} variante${variants.length !== 1 ? 's' : ''}` : ''}`}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {tab === 'info' && (
        <FloatingSaveBar
          dirty={dirty}
          saving={saving}
          saved={saved}
          onSave={handleSubmit}
          label={isEdit ? 'Guardar cambios' : 'Crear producto'}
        />
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Products() {
  const [products,       setProducts]       = useState<Product[]>([]);
  const [categories,     setCategories]     = useState<Category[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filterCat,      setFilterCat]      = useState('');
  const [filterShipping, setFilterShipping] = useState<'all'|'yes'|'no'>('all');
  const [filterStock,    setFilterStock]    = useState<'all'|'low'|'out'>('all');
  const [modalProduct,   setModalProduct]   = useState<Product | null | 'new'>(null);
  const [deleteTarget,   setDeleteTarget]   = useState<Product | null>(null);
  const [deleting,       setDeleting]       = useState(false);
  const [tourRun,        setTourRun]        = useState(false);
  const [groups,         setGroups]         = useState<OptionGroupDto[]>([]);
  const [listError,      setListError]      = useState('');
  const { kitchenEnabled } = useStoreFlags();

  useEffect(() => {
    if (!kitchenEnabled) return;
    getOptionGroups()
      .then(res => setGroups(res.data))
      .catch(err => setListError(serverMessage(err, 'No se pudieron cargar los grupos de opciones')));
  }, [kitchenEnabled]);

  // Agotado rápido (solo preparados): optimista y vuelta atrás si falla.
  const handleToggleAvailable = async (p: Product) => {
    const next = p.available === false;
    setProducts(prev => prev.map(x => x.productId === p.productId ? { ...x, available: next } : x));
    try {
      await setProductAvailability(p.productId, next);
    } catch (err) {
      setProducts(prev => prev.map(x => x.productId === p.productId ? { ...x, available: !next } : x));
      setListError(serverMessage(err, `No se pudo cambiar "${p.name}"`));
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('tour-product-done')) setTourRun(true);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getProducts(), getCategories()])
      .then(([prodRes, catRes]) => {
        setProducts(prodRes.data);
        setCategories(catRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (saved: Product) => {
    setProducts(prev => {
      const exists = prev.find(p => p.productId === saved.productId);
      return exists
        ? prev.map(p => p.productId === saved.productId ? saved : p)
        : [saved, ...prev];
    });
  };

  const handleCategoryCreated = (cat: Category) => {
    setCategories(prev => [...prev, cat].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteProduct(deleteTarget.productId);
    setProducts(prev => prev.filter(p => p.productId !== deleteTarget.productId));
    setDeleteTarget(null);
    setDeleting(false);
  };

  const filtered = products.filter(p => {
    const q           = search.toLowerCase();
    const matchSearch = !q ||
      p.name.toLowerCase().includes(q) ||
      (p.sku ?? '').toLowerCase().includes(q) ||
      (p.description ?? '').replace(/<[^>]+>/g, ' ').toLowerCase().includes(q) ||
      p.variants?.some(v =>
        v.name.toLowerCase().includes(q) ||
        Object.values(v.attributes ?? {}).some(a => a.toLowerCase().includes(q))
      );
    const matchCat      = !filterCat || p.categoryId === filterCat;
    const matchShipping = filterShipping === 'all' ||
      (filterShipping === 'yes' && p.hasShipping) ||
      (filterShipping === 'no' && !p.hasShipping);
    const totalStock    = p.hasVariants && p.variants?.length
      ? p.variants.filter(v => v.isActive).reduce((s, v) => s + v.stock, 0)
      : p.stock;
    // Los preparados no llevan inventario: no cuentan como "sin stock".
    const matchStock    = filterStock === 'all' || (!isPrepared(p) && (
      (filterStock === 'out' && totalStock === 0) ||
      (filterStock === 'low' && totalStock > 0 && totalStock < 5)));
    return matchSearch && matchCat && matchShipping && matchStock;
  });

  const statsTotal    = products.length;
  const statsLowStock = products.filter(p => {
    if (isPrepared(p)) return false;
    const s = p.hasVariants ? p.variants?.filter(v => v.isActive).reduce((a, v) => a + v.stock, 0) : p.stock;
    return (s ?? 0) > 0 && (s ?? 0) < 5;
  }).length;
  const statsOutStock = products.filter(p => {
    if (isPrepared(p)) return false;
    const s = p.hasVariants ? p.variants?.filter(v => v.isActive).reduce((a, v) => a + v.stock, 0) : p.stock;
    return (s ?? 0) === 0;
  }).length;

  return (
    <div className="min-h-screen bg-canvas">
      <div className="bg-surface border-b border-border-subtle px-6 py-5 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div>
              <h1 className="text-xl font-bold text-txt-primary">Productos</h1>
              <p className="text-sm text-txt-tertiary mt-0.5">
                {loading ? '...' : `${statsTotal} producto${statsTotal !== 1 ? 's' : ''} activo${statsTotal !== 1 ? 's' : ''}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setModalProduct('new')} data-tour="product-new"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-[#0A0A0F]"
                style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}>
                <PlusIcon /> Nuevo producto
              </button>
              <button onClick={() => setTourRun(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-border-default text-txt-secondary hover:bg-surface-overlay transition"
                title="Ver tutorial paso a paso">
                <HelpCircle size={15} /> ¿Cómo funciona?
              </button>
            </div>
          </div>

          {!loading && (
            <div className="flex gap-4 mb-4">
              <div className="text-xs text-txt-secondary">
                <span className="font-semibold text-txt-primary">{statsTotal}</span> total
              </div>
              {statsLowStock > 0 && (
                <div className="text-xs text-orange-600">
                  <span className="font-semibold">{statsLowStock}</span> con stock bajo
                </div>
              )}
              {statsOutStock > 0 && (
                <div className="text-xs text-red-500">
                  <span className="font-semibold">{statsOutStock}</span> sin stock
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-tertiary"><SearchIcon /></span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por nombre, SKU, atributo..."
                className="pl-9 pr-4 py-2 text-sm border border-border-default bg-surface-elevated text-txt-primary placeholder:text-txt-tertiary rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 transition w-64" />
            </div>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-secondary">
              <option value="">Todas las categorías</option>
              {categories.map(c => <option key={c.categoryId} value={c.categoryId}>{c.name}</option>)}
            </select>
            <select value={filterShipping} onChange={e => setFilterShipping(e.target.value as any)}
              className="px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-secondary">
              <option value="all">Todos</option>
              <option value="yes">Con envío</option>
              <option value="no">Sin envío</option>
            </select>
            <select value={filterStock} onChange={e => setFilterStock(e.target.value as any)}
              className="px-3 py-2 text-sm border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-lime/30 bg-surface text-txt-secondary">
              <option value="all">Todo stock</option>
              <option value="low">Stock bajo (&lt;5)</option>
              <option value="out">Sin stock</option>
            </select>
            {(search || filterCat || filterShipping !== 'all' || filterStock !== 'all') && (
              <button onClick={() => { setSearch(''); setFilterCat(''); setFilterShipping('all'); setFilterStock('all'); }}
                className="text-xs text-blue-600 hover:underline">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {listError && (
          <div role="alert" className="mb-4 rounded-2xl border border-error/40 bg-error/10 px-4 py-3 text-sm text-error flex items-start justify-between gap-3">
            <span>{listError}</span>
            <button onClick={() => setListError('')} className="text-xs font-semibold">Cerrar</button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <svg className="animate-spin text-blue-600" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-txt-tertiary">
            <BoxIcon />
            <p className="text-sm">
              {search || filterCat || filterShipping !== 'all' || filterStock !== 'all'
                ? 'Sin resultados para este filtro'
                : 'Sin productos aún'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(p => (
              <ProductCard key={p.productId} product={p}
                onEdit={prod => setModalProduct(prod)}
                onDelete={setDeleteTarget}
                onToggleAvailable={kitchenEnabled ? handleToggleAvailable : undefined} />
            ))}
          </div>
        )}
      </div>

      {modalProduct !== null && (
        <ProductModal
          product={modalProduct === 'new' ? null : modalProduct}
          categories={categories}
          onClose={() => setModalProduct(null)}
          onSaved={handleSaved}
          onCategoryCreated={handleCategoryCreated}
          kitchenEnabled={kitchenEnabled}
          groups={groups}
        />
      )}

      <GuidedTour
        steps={PRODUCTS_TOUR}
        run={tourRun}
        onFinish={() => { setTourRun(false); localStorage.setItem('tour-product-done', '1'); }}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
          <div className="bg-surface rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-txt-primary mb-2">¿Eliminar producto?</h3>
            <p className="text-sm text-txt-secondary mb-5">
              <span className="font-semibold">{deleteTarget.name}</span> será desactivado junto con todas sus variantes.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-surface-overlay text-txt-secondary hover:bg-border-default transition">
                Cancelar
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-50">
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

